<template>
  <AIVDialog
    v-model="isVisible"
    title="Medication Administration"
    width="40%"
    height="80vh"
    class="medication-admin-modal"
  >
    <div v-if="loading" class="text-center py-3">
      <i class="fa-solid fa-spinner fa-spin"></i> Loading medications...
    </div>

    <div v-else-if="clientMedications.length === 0" class="text-center py-3">
      <p style="color: #e0e0e0;">No active medications found for this client.</p>
    </div>

    <div v-else>
      <div v-for="med in clientMedications" :key="med.id" class="medication-admin-item mb-2">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <strong>{{ med.name }}</strong> - {{ med.dosage }}
            <span v-if="med.frequency">({{ med.frequency }})</span>
          </div>
          <el-switch
            v-model="administrations[med.id].wasAdministered"
            active-text="Given"
            inactive-text="Skipped"
          />
        </div>
        <div v-if="!administrations[med.id].wasAdministered" class="mt-1">
          <el-input
            v-model="administrations[med.id].skipReason"
            placeholder="Reason for skipping (required)"
            size="small"
          />
        </div>
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <el-button round @click="isVisible = false">Cancel</el-button>
        <el-button type="primary" round @click="confirm" :disabled="loading || clientMedications.length === 0">
          Confirm
        </el-button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import AIVDialog from "@/components/AIVDialog.vue";
import { clientsService } from "@/serviceHandlers/clientsService";

export default {
  name: "MedicationAdminModal",
  components: { AIVDialog },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    shift: {
      type: Object,
      default: () => ({}),
    },
    userId: {
      type: [String, Number],
      default: null,
    },
    previousAdministrations: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      loading: false,
      clientMedications: [],
      administrations: {},
    };
  },

  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      },
    },
  },

  watch: {
    visible(newVal) {
      if (newVal) {
        this.fetchMedications();
      }
    },
  },

  methods: {
    async fetchMedications() {
      if (!this.shift || this.shift.payer === "Non Service") return;
      const shiftClients = this.shift.clients || this.shift?.originalData?.clients || [];
      if (shiftClients.length === 0) return;
      const clientId = shiftClients[0].id;
      if (!clientId) return;

      this.loading = true;
      try {
        const response = await clientsService.getMedications(clientId);
        // Filter by active status and by schedule_days (current day of week)
        const currentDayOfWeek = new Date().getDay(); // 0=Sun..6=Sat
        this.clientMedications = (response.medications || []).filter(m => {
          if (m.active === false) return false;
          if (m.schedule_days && Array.isArray(m.schedule_days) && m.schedule_days.length > 0) {
            return m.schedule_days.includes(currentDayOfWeek);
          }
          return true;
        });

        const administrations = {};
        for (const med of this.clientMedications) {
          if (this.previousAdministrations[med.id]) {
            // Restore previous entries
            administrations[med.id] = { ...this.previousAdministrations[med.id] };
          } else {
            administrations[med.id] = {
              clientId: clientId,
              medicationId: med.id,
              wasAdministered: true,
              skipReason: "",
              administeredAt: null,
            };
          }
        }
        this.administrations = administrations;
      } catch (error) {
        this.clientMedications = [];
      } finally {
        this.loading = false;
      }
    },

    confirm() {
      // Validate skip reasons
      for (const med of this.clientMedications) {
        const ma = this.administrations[med.id];
        if (ma && !ma.wasAdministered && !ma.skipReason.trim()) {
          this.$notifyError(`Please provide a reason for skipping "${med.name}"`);
          return;
        }
      }

      // Set administeredAt for given meds
      const result = {};
      for (const med of this.clientMedications) {
        const ma = { ...this.administrations[med.id] };
        if (ma.wasAdministered) {
          ma.administeredAt = new Date().toISOString();
        }
        result[med.id] = ma;
      }

      this.$emit("medication-save", result);
      this.isVisible = false;
    },
  },
};
</script>

<style scoped>
.medication-admin-modal :deep(.el-dialog) {
  display: flex !important;
  flex-direction: column !important;
  max-height: 80vh !important;
}

.medication-admin-modal :deep(.el-dialog__header) {
  flex-shrink: 0 !important;
}

.medication-admin-modal :deep(.el-dialog__body) {
  flex: 1 1 auto !important;
  overflow-y: auto !important;
  padding: 20px !important;
  min-height: 0 !important;
}

.medication-admin-modal :deep(.el-dialog__footer) {
  flex-shrink: 0 !important;
}

.medication-admin-item {
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
</style>
